import { useTikTokSpecialPermissions, useUserRoles } from '@anytag/auth';
import {
  WarningReason,
  useTiktokSpecialPostQuery,
  useTiktokSpecialPostHistoryQuery,
  TikTokSpecialCampaignPostStatus
} from '@anytag/graphql';
import { Button, Drawer, Form, RenderDataWithFallback } from '@anytag/shared-atoms';
import { useQueryHelper } from '@anytag/shared-hooks';
import { useRejectTikTokCampaignPost } from '../hooks';
import { useUpdateTikTokPostsStatus } from '../TikTokCampaignPostsList/hooks';
import { HistorySection } from './HistorySection';
import { PostInfoSection } from './PostInfoSection';
import { RequestModificationMessage } from './RequestModification';
import { SparkAdsSettings } from './SparkAdsSettings';

export interface DefaultTemplateProps {
  postId: number;
  onClose: () => void;
  campaignFinished: boolean;
  campaignId: number;
}

export const DefaultTemplate = ({ postId, onClose, campaignFinished, campaignId }: DefaultTemplateProps) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { isAdvertiserFromTikTok, isAdminStaff } = useUserRoles();
  const { handleRejectPost, RejectMarketplacePostModal } = useRejectTikTokCampaignPost();

  const { hideApprovePostBtn, hideRejectInappropriatePostBtn } = useTikTokSpecialPermissions();
  const { updating, handleSelectStatus, UpdateTikTokSpecialPostStatusModal, callUpdateTiktokSpecialPostStatus } =
    useUpdateTikTokPostsStatus(campaignId);

  const { data, loading } = useTiktokSpecialPostQuery({
    variables: { pk: postId },
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
      onClose?.();
    }
  });
  const { data: historyData, loading: fetchingHistory } = useTiktokSpecialPostHistoryQuery({
    variables: { pk: postId },
    fetchPolicy: 'cache-and-network',
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
      onClose?.();
    }
  });

  const tiktokSpecialPost = data?.tiktokSpecialPost;

  if (!tiktokSpecialPost) {
    return null;
  }

  const { images, status, caption, reason, postUrl, postedDate, warningReason, detailedReason, influencer } =
    tiktokSpecialPost;
  const hasRequestModificationButton =
    !hideRejectInappropriatePostBtn &&
    (isAdminStaff || isAdvertiserFromTikTok) &&
    status &&
    [TikTokSpecialCampaignPostStatus.EFFECTIVE, TikTokSpecialCampaignPostStatus.WARNING_SOLVED].includes(status);
  const hasWarningSolvedButton =
    !hideRejectInappropriatePostBtn &&
    status &&
    status === TikTokSpecialCampaignPostStatus.WARNING &&
    warningReason === WarningReason.INVALID_CONTENT;
  const hasApproveButton = !hideApprovePostBtn && status === TikTokSpecialCampaignPostStatus.EFFECTIVE;

  const handleSetWarningSolvedStatus = async () => {
    try {
      await callUpdateTiktokSpecialPostStatus({
        variables: {
          input: {
            campaignId,
            postIds: [postId],
            status: TikTokSpecialCampaignPostStatus.WARNING_SOLVED
          }
        }
      });

      onClose();
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  };

  return (
    <>
      <Drawer.Root className="w-129" onClose={onClose} title={t('Post Detail')} onInteractOutside={onClose} open>
        <RenderDataWithFallback loading={loading || fetchingHistory}>
          <Drawer.Body>
            <div className="pb-6">
              <PostInfoSection
                caption={caption}
                postUrl={postUrl}
                postedDate={postedDate}
                images={[...(images || [])]}
                influencer={{
                  id: influencer.id,
                  name: influencer.name,
                  avatar: influencer.avatar,
                  followers: influencer.followersCount
                }}
              />

              {detailedReason || reason ? (
                <RequestModificationMessage message={detailedReason || reason} />
              ) : hasRequestModificationButton ? (
                <Button
                  variant="outline"
                  prefixIcon={{ icon: 'send', size: 12 }}
                  title={t('Button.Request modification')}
                  className="mt-4 w-full"
                  onClick={() => handleRejectPost(tiktokSpecialPost)}
                />
              ) : null}
              <Form.Line className="my-4" />
              <SparkAdsSettings
                postSystemId={tiktokSpecialPost.id}
                boostedInfo={tiktokSpecialPost.boostedInfo}
                draftBoostedInfo={tiktokSpecialPost.draftBoostedInfo}
                campaignFinished={campaignFinished}
              />
            </div>
            <HistorySection data={historyData?.tiktokSpecialPostHistory} />
          </Drawer.Body>

          <Drawer.Footer>
            {hasApproveButton ? (
              <Form.Footer className="sticky bottom-0 z-5 justify-between bg-white">
                <p className="max-w-51 text-sm text-tertiary">
                  {t('You can send feedback to creator via Email and AnyCreator')}
                </p>
                <Button
                  variant="solid"
                  loading={updating}
                  title={t('Button.Approve Post')}
                  className="min-w-unset ml-auto px-12"
                  onClick={() => (status ? handleSelectStatus(status, [tiktokSpecialPost]) : undefined)}
                />
              </Form.Footer>
            ) : hasWarningSolvedButton ? (
              <Form.Footer className="sticky bottom-0 z-5 justify-between bg-white">
                <Button
                  variant="solid"
                  loading={updating}
                  title={t('Button.Make effective')}
                  className="min-w-unset ml-auto px-12"
                  onClick={handleSetWarningSolvedStatus}
                />
              </Form.Footer>
            ) : null}
          </Drawer.Footer>
        </RenderDataWithFallback>
      </Drawer.Root>

      <RejectMarketplacePostModal />
      <UpdateTikTokSpecialPostStatusModal />
    </>
  );
};
